import PropTypes from 'prop-types';
import { useMutation, useApolloClient } from '@apollo/client';
import { UNIT_TYPES } from '../../../../consts';
import { getUnitForType } from '../../../../Helpers/unit';
import { formatIsoDate } from '../../../../Helpers/dateFormatter';
import { useParams } from 'react-router-dom';
import ValidationErrors from '../../../../Validation/ValidationErrors';
import MeasurementGroupSaveMutation from './MeasurementGroupSaveMutation';
import MeasurementFetchByDateQuery from '../MeasurementFetchByDate/MeasurementFetchByDateQuery';
import DietSettingsMeasurementsComponentQuery from '../../DietSettingsMeasurementsComponentQuery';
import clearDailyDietPlanCacheObjects from '../../../../Apollo/clearDailyDietPlanCacheObjects';

const MeasurementSaveByDateConnectorApollo = ({ children, post }) => {
  const client = useApolloClient();
  const [saveMeasurements, { loading }] = useMutation(MeasurementGroupSaveMutation);
  const params = useParams();

  const createSaveMeasurements = async (formData) => {
    // todo: get id from cache
    const serializedState = client.cache.extract();
    const userId = Object.values(serializedState).filter((item) => item.__typename === 'Me')[0].id;

    const request = {
      date: formData.date,
    };

    if (formData.weight != null) {
      request.weight = {
        value: Number(formData.weight),
        unit: getUnitForType(UNIT_TYPES.BODY_WEIGHT, formData.systemOfMeasures),
      };
    }

    if (formData.waist != null) {
      request.waist = {
        value: Number(formData.waist),
        unit: getUnitForType(UNIT_TYPES.LENGTH_BODY_MEASUREMENT, formData.systemOfMeasures),
      };
    }

    if (formData.hip != null) {
      request.hip = {
        value: Number(formData.hip),
        unit: getUnitForType(UNIT_TYPES.LENGTH_BODY_MEASUREMENT, formData.systemOfMeasures),
      };
    }

    if (formData.thigh != null) {
      request.thigh = {
        value: Number(formData.thigh),
        unit: getUnitForType(UNIT_TYPES.LENGTH_BODY_MEASUREMENT, formData.systemOfMeasures),
      };
    }

    if (formData.rightthigh != null) {
      request.rightthigh = {
        value: Number(formData.rightthigh),
        unit: getUnitForType(UNIT_TYPES.LENGTH_BODY_MEASUREMENT, formData.systemOfMeasures),
      };
    }

    if (formData.chest != null) {
      request.chest = {
        value: Number(formData.chest),
        unit: getUnitForType(UNIT_TYPES.LENGTH_BODY_MEASUREMENT, formData.systemOfMeasures),
      };
    }

    if (formData.arm != null) {
      request.arm = {
        value: Number(formData.arm),
        unit: getUnitForType(UNIT_TYPES.LENGTH_BODY_MEASUREMENT, formData.systemOfMeasures),
      };
    }

    if (formData.rightarm != null) {
      request.rightarm = {
        value: Number(formData.rightarm),
        unit: getUnitForType(UNIT_TYPES.LENGTH_BODY_MEASUREMENT, formData.systemOfMeasures),
      };
    }

    const response = await saveMeasurements({
      variables: { request },
      update: (cache) => {
        cache.modify({
          id: `Me:${userId}`,
          fields: {
            isTimeToNagForCurrentMeasurement() {
              return false;
            },
          },
        });
      },
      refetchQueries: [
        {
          query: MeasurementFetchByDateQuery,
          variables: {
            date: params.date ? params.date : formatIsoDate(new Date()),
          },
        },
        {
          query: DietSettingsMeasurementsComponentQuery,
        },
      ],
      awaitRefetchQueries: true,
    });

    client.cache.evict({
      id: 'UserMeasurement:{}',
    });

    client.cache.gc();

    if (response.data.me.measurementGroupSave.__typename === 'ValidationException') {
      throw new ValidationErrors(
        response.data.me.measurementGroupSave.messages,
        response.data.me.measurementGroupSave.details
      );
    }

    let goalLostBecauseOfLoseWeight = false;
    let goalLostBecauseOfPutOnWeight = false;
    let goalReachedBecauseOfLoseWeight = false;
    let goalReachedBecauseOfPutOnWeight = false;

    goalLostBecauseOfLoseWeight =
      response.data.me.measurementGroupSave.goal.lostBecauseOfLoseWeight;
    goalLostBecauseOfPutOnWeight =
      response.data.me.measurementGroupSave.goal.lostBecauseOfPutOnWeight;
    goalReachedBecauseOfLoseWeight =
      response.data.me.measurementGroupSave.goal.reachedBecauseOfLoseWeight;
    goalReachedBecauseOfPutOnWeight =
      response.data.me.measurementGroupSave.goal.reachedBecauseOfPutOnWeight;

    const code = response.data.me.measurementGroupSave.code;

    clearDailyDietPlanCacheObjects(client);

    // broadcast channel
    if (post) {
      post('reload');
    }

    return {
      goalLostBecauseOfLoseWeight,
      goalLostBecauseOfPutOnWeight,
      goalReachedBecauseOfLoseWeight,
      goalReachedBecauseOfPutOnWeight,
      code,
    };
  };

  return children({
    saveMeasurements: createSaveMeasurements,
    saving: loading,
  });
};

MeasurementSaveByDateConnectorApollo.propTypes = {
  children: PropTypes.func.isRequired,
};

export default MeasurementSaveByDateConnectorApollo;
